<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-clipboard-check me-2"></i>Stock Takes Management
      </h4>
      <div class="d-flex">
        <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary"
            @click="stockTakeModal = true; stockTakeMode = 'add'">
          <i class="bi bi-plus-circle me-1"></i> Add Stock Take
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading stock takes...</p>
      </div>

      <!-- Stock Takes Content -->
      <div v-if="!loading" class="stock-takes-content fade-in">
        <!-- Stock Takes Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ stockTakes.total }}</span>
            <span class="text-muted">Stock Takes</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search stock take" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Stock Takes Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>CODE</th>
                  <th>STORE</th>
                  <th>BY USER</th>
                  <th>DESCRIPTION</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stockTake, index) in stockTakes.data" :key="stockTake.id" class="stock-take-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ stockTake.id.split("-").pop() }}</td>
                  <td>{{ stockTake.code }}</td>
                  <td>{{ stockTake.store.code }} - {{ stockTake.store.name }}</td>
                  <td>{{ stockTake.user.first_name }} {{ stockTake.user.last_name }}</td>
                  <td>{{ stockTake.description }}</td>
                  <td>
                    <Badge pill :type="getPillByStatus(stockTake.status)">
                      {{ stockTake.status }}
                    </Badge>
                  </td>
                  <td>{{ formatDate(stockTake.created_at) }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary" @click="stockTakeDetails(stockTake.id)">
                      <i class="bi bi-eye"></i> Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <!-- Add StockTake Modal -->
    <Modal :open="stockTakeModal" @closed="stockTakeModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0" v-if="stockTakeMode === 'add'">
          <i class="bi bi-plus-circle me-2"></i>Add Stock Take
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2"></i>Edit Stock Take
        </h5>
        <button @click="stockTakeModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label for="code" class="form-label fw-bold">Stock Take Code:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-upc"></i></span>
                  <input type="text" class="form-control" placeholder="Code" v-model="stockTake.code" readonly>
                </div>
                <small class="form-text text-muted">Auto-generated code</small>
              </div>
            </div>
            <div class="col-md-8">
              <div class="form-group mb-3">
                <label for="description" class="form-label fw-bold">Description:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-card-text"></i></span>
                  <textarea class="form-control" placeholder="Enter stock take description" v-model="stockTake.description" rows="3"></textarea>
                </div>
                <small class="form-text text-muted">Provide details about this stock take</small>
              </div>
            </div>
          </div>

          <!-- Summary Section -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="alert alert-info d-flex align-items-center">
                <i class="bi bi-info-circle-fill me-2"></i>
                <div>
                  <strong>Note:</strong> After creating the stock take, you'll be able to add products to it.
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="stockTakeModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" v-if="stockTakeMode === 'add'" @click="createStockTake">
          <i class="bi bi-plus-circle me-1"></i> Create Stock Take
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateStockTake(stockTake.id)">
          <i class="bi bi-check-circle me-1"></i> Update Stock Take
        </button>
      </div>
    </Modal>
    <!-- Add StockTake Modal End -->

  </Card>
</template>

<script>
import stockTakeService from "@/api/stockTakeService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import formatDate from "@/utils/formatDate";
import loadingButton from "@/utils/loadingButton";

export default {
  name: "StockTakes",

  data(){
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    return {
      organization: {},
      store: {},
      profile: {},
      loading: true,
      stockTake: {
        organization_id: this.organization.id,
        store_id: this.store.id,
        user_id: this.profile.id,
      },
      stockTakeMode: 'add',
      stockTakeModal: false,
      stockTakes: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    formatDate,
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'pending':
          return 'warning';
        case 'complete':
          return 'success';
      }
    },
    async getStockTakes() {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      this.stockTakes = await stockTakeService.getStockTakes(this.store.id, params)
      let nextNo = await stockTakeService.getStockTakeNextNo(this.store.id)
      this.stockTake.code = nextNo.next
      this.loading = false
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getStockTakes()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getStockTakes()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    stockTakeDetails(id) {
      router.push('/stock-take/' + id)
    },
    async createStockTake(e) {
      loadingButton(e)
      let response = await stockTakeService.createStockTake(this.stockTake)
      if (response) {
        this.stockTakeModal = false
        await this.getStockTakes()
      }
      loadingButton(e, false)
    },
  },

  computed: {
    totalPages(){
      return this.stockTakes?.last_page || 1
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getStockTakes()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getStockTakes()
    },
  },

  created: async function () {
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
    await this.getStockTakes()
  },

}
</script>

<style scoped>
/* Stock Takes page styles */
.stock-takes-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.stock-take-row {
  transition: all 0.2s ease;
}

.stock-take-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
